import {
	rosterStaffRole as staffRole,
	rosterStaffPriority as staffPriority,
} from "@/utils/functions";
import RosterCondition from "./RosterCondition";
import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import moment from "moment";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SvgIcon from "@mui/material/SvgIcon";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {CalendarEventsAPI} from "@/api/calendarEvents";
import {RosterAPI} from "@/api/roster";
import SeasonTimeline from "@/components/SeasonTimeline";
import "./panorama.scss";

export default function RosterPanorama({
	team,
	matches,
	navigate,
	selectTab,
	identityHeader,
}) {
	const [trainings, setTrainings] = useState([]);
	const [matrix, setMatrix] = useState(null);
	const [loading, setLoading] = useState(true);
	const [errors, setErrors] = useState({});
	const now = moment();
	const fromDate = now.clone().subtract(6, "days").format("YYYY-MM-DD");
	const toDate = now.format("YYYY-MM-DD");
	useEffect(() => {
		if (!team?.id || !team?.season?.id) return;
		let active = true;
		setLoading(true);
		setErrors({});
		setTrainings([]);
		setMatrix(null);
		Promise.allSettled([
			CalendarEventsAPI.list({
				seasonId: team.season.id,
				rosterId: team.id,
				eventType: "training",
			}),
			RosterAPI.activityMatrix(team.id, {kind: "training", fromDate, toDate}),
		])
			.then(([calendar, activity]) => {
				if (!active) return;
				if (calendar.status === "fulfilled")
					setTrainings(calendar.value?.trainings || []);
				if (activity.status === "fulfilled") setMatrix(activity.value);
				setErrors({
					calendar: calendar.status === "rejected",
					activity: activity.status === "rejected",
				});
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [team?.id, team?.season?.id, fromDate, toDate]);
	const players = team?.players || [];
	const staff = [...(team?.staff || [])].sort(
		(a, b) => staffPriority(a) - staffPriority(b),
	);
	const modules = team?.modules || [];
	const events = [
		...matches.map((match) => {
			const home = match.homeRosterId === team.id;
			const scored =
				Number.isInteger(match.homeClubGoals) &&
				Number.isInteger(match.awayClubGoals);
			return {
				...match,
				kind: "match",
				title: `${match.homeTeamName || match.homeClub || "—"} Vs ${match.awayTeamName || match.awayClub || "—"}`,
				detail: match.competitionName || "Competizione non indicata",
				score: scored ? `${match.homeClubGoals}–${match.awayClubGoals}` : null,
				outcome: scored
					? (home
							? match.homeClubGoals - match.awayClubGoals
							: match.awayClubGoals - match.homeClubGoals) > 0
						? "win"
						: match.homeClubGoals === match.awayClubGoals
							? "draw"
							: "loss"
					: "",
			};
		}),
		...trainings.map((training) => ({
			...training,
			kind: "training",
			title: training.description || "Allenamento",
			detail: training.fieldName || "Campo non indicato",
		})),
	]
		.filter((event) => moment(event.startDate).isValid())
		.sort((a, b) => moment(a.startDate).diff(moment(b.startDate)));
	const isEnded = (event) =>
		moment(event.endDate || event.startDate).isSameOrBefore(now) &&
		(!event.startTimeToBeConfirmed ||
			moment(event.startDate).isBefore(now, "day"));
	const recent = events.filter(isEnded).slice(-3).reverse();
	const upcoming = events.filter((event) => !isEnded(event)).slice(0, 3);
	const columns = (matrix?.columns || []).filter((column) => !column.scheduled);
	let expected = 0,
		present = 0,
		known = 0;
	for (const player of matrix?.players || [])
		for (const column of columns) {
			const status = player.cells?.[column.key]?.status;
			if (status !== "not-applicable") expected++;
			if (status === "present") present++;
			if (["present", "absent", "injured"].includes(status)) known++;
		}

	const totals = matrix?.trainingAnalysis?.period?.completed;
	const recentMatches = matches.filter(
		(match) =>
			moment(match.startDate).isSameOrAfter(moment(fromDate)) && isEnded(match),
	);
	const nextMatch = matches
		.filter(
			(match) =>
				(match.homeRosterId === team.id || match.awayRosterId === team.id) &&
				!(
					Number.isInteger(match.homeClubGoals) &&
					Number.isInteger(match.awayClubGoals)
				) &&
				moment(match.startDate).isValid() &&
				(moment(match.startDate).isAfter(now) ||
					(match.startTimeToBeConfirmed &&
						moment(match.startDate).isSame(now, "day"))),
		)
		.sort((a, b) => moment(a.startDate).diff(moment(b.startDate)))[0];
	const daysToMatch = nextMatch
		? moment(nextMatch.startDate)
				.startOf("day")
				.diff(now.clone().startOf("day"), "days")
		: null;
	const nextOpponent = nextMatch
		? nextMatch.homeRosterId === team.id
			? nextMatch.awayTeamName || nextMatch.awayClub
			: nextMatch.homeTeamName || nextMatch.homeClub
		: "";
	const eventList = (list, future) => (
		<section className="roster-panorama__panel">
			<header>
				<h3>{future ? "Prossimi impegni" : "Attività recente"}</h3>
				<span>{future ? "In calendario" : "Ultimi appuntamenti"}</span>
			</header>
			{errors.calendar && (
				<p role="alert">
					Allenamenti non disponibili. Sono mostrate le sole partite.
				</p>
			)}
			{loading && <p role="status">Caricamento appuntamenti…</p>}
			{!loading && !list.length && (
				<p>
					Nessun appuntamento {future ? "programmato" : "trascorso"} nella
					stagione.
				</p>
			)}
			<div className="roster-panorama__events">
				{list.map((event) => (
					<Link
						key={`${event.kind}-${event.id}`}
						to={`/${event.kind === "match" ? "matches" : "trainings"}/${event.id}`}
						className={`roster-panorama__event roster-panorama__event--${event.kind} ${event.outcome ? `roster-panorama__event--${event.outcome}` : ""}`}
					>
						<time dateTime={event.startDate}>
							<strong>{moment(event.startDate).format("DD MMM")}</strong>
							<span>
								{event.startTimeToBeConfirmed
									? "OdD"
									: moment(event.startDate).format("HH:mm")}
							</span>
						</time>
						{event.kind === "match" ? (
							<SportsSoccerIcon />
						) : (
							<SvgIcon>
								<path
									fillRule="evenodd"
									d="M10 2h4l5 17H5L10 2Zm-.6 6-.6 2h6.4l-.6-2H9.4Zm-1.5 5-.6 2h9.4l-.6-2H7.9ZM3 20h18v2H3z"
								/>
							</SvgIcon>
						)}
						<div>
							<strong>{event.title}</strong>
							<span>{event.detail}</span>
						</div>
						{event.score && <b>{event.score}</b>}
						<ArrowForwardIcon className="roster-panorama__arrow" />
					</Link>
				))}
			</div>
		</section>
	);
	return (
		<div className="roster-panorama">
			{identityHeader}
			<div className="roster-panorama__composition">
				<section className="roster-panorama__panel">
					<header>
						<h3>
							<GroupsOutlinedIcon /> Rosa
						</h3>
						<button onClick={() => selectTab("players")}>
							Giocatori <ArrowForwardIcon />
						</button>
					</header>
					<div className="roster-panorama__numbers">
						<div>
							<strong>{players.length}</strong>
							<span>giocatori assegnati</span>
						</div>
						<div>
							<strong>
								{players.filter((entry) => entry.player?.goalkeeper).length}
							</strong>
							<span>portieri</span>
						</div>
					</div>
					<p>
						Moduli configurati:{" "}
						{modules.length
							? modules
									.map((entry) => entry.module?.name || "Modulo senza nome")
									.join(" · ")
							: "nessuno"}
					</p>
				</section>
				<section className="roster-panorama__panel">
					<header>
						<h3>Staff della rosa</h3>
						<button onClick={() => selectTab("staffs")}>
							Staff <ArrowForwardIcon />
						</button>
					</header>
					<div className="roster-panorama__staff">
						{staff.length ? (
							staff.slice(0, 4).map((entry) => (
								<div key={entry.staffId || entry.staff?.id}>
									<strong>
										{[entry.staff?.firstName, entry.staff?.lastName]
											.filter(Boolean)
											.join(" ") || "Nome non indicato"}
									</strong>
									<span>{staffRole(entry) || "Ruolo non indicato"}</span>
								</div>
							))
						) : (
							<p>Nessun membro dello staff assegnato.</p>
						)}
					</div>
					{staff.length > 4 && (
						<p>Altri {staff.length - 4} membri nella scheda Staff.</p>
					)}
				</section>
				<RosterCondition team={team} fromDate={fromDate} toDate={toDate} />
			</div>
			<section className="roster-panorama__panel">
				<header>
					<h3>Ultimi 7 giorni</h3>
					<span>
						{moment(fromDate).format("DD MMM")} – {now.format("DD MMM")}
					</span>
				</header>
				{errors.activity ? (
					<p role="alert">Riepilogo allenamenti non disponibile.</p>
				) : loading ? (
					<p role="status">Caricamento riepilogo…</p>
				) : (
					<>
						<div className="roster-panorama__week">
							{[
								{value: columns.length, label: "Sedute concluse"},
								{
									value:
										totals?.minutes == null
											? "—"
											: `${totals.minutes}${totals.missingDuration || totals.sessionsWithoutExercises ? "*" : ""}`,
									label: "Minuti di esercizi",
								},
								{value: `${present}/${expected}`, label: "Presenze su attese"},
								{value: recentMatches.length, label: "Gare trascorse"},
							].map((metric) => (
								<div key={metric.label}>
									<strong>{metric.value}</strong>
									<span>{metric.label}</span>
								</div>
							))}
							{nextMatch ? (
								<Link
									className="roster-panorama__next-kpi"
									to={`/matches/${nextMatch.id}`}
									title={`${nextOpponent || "Avversario da definire"} · ${moment(nextMatch.startDate).format("DD/MM/YYYY")} · ${nextMatch.startTimeToBeConfirmed ? "Orario da definire" : moment(nextMatch.startDate).format("HH:mm")}`}
								>
									<strong>
										{daysToMatch === 0 ? (
											"Oggi"
										) : (
											<>
												{daysToMatch}
												<small>
													{daysToMatch === 1 ? " giorno" : " giorni"}
												</small>
											</>
										)}
									</strong>
									<span>Alla prossima partita</span>
								</Link>
							) : (
								<div className="roster-panorama__next-kpi">
									<strong>—</strong>
									<span>Nessuna partita in programma</span>
								</div>
							)}
						</div>
						<details className="roster-panorama__reading">
							<summary>Come leggere i dati</summary>
							<p className="roster-panorama__note">
								Solo attività concluse. Minuti degli esercizi registrati, senza
								preparazione aggiunta. Presenze registrate: {known}/{expected};
								rosa attuale e partecipanti delle sedute, senza ricostruzione
								storica.{" "}
								{totals?.sessionsWithoutExercises > 0 &&
									`${totals.sessionsWithoutExercises} sedute senza esercizi.`}{" "}
								{(totals?.missingDuration > 0 ||
									totals?.sessionsWithoutExercises > 0) &&
									"* Totale minuti parziale."}{" "}
							</p>
						</details>
					</>
				)}
			</section>
			<div className="roster-panorama__appointments">
				{eventList(recent, false)}
				{eventList(upcoming, true)}
			</div>
			<section className="roster-overview__timeline">
				<h3>La stagione gara per gara</h3>
				<SeasonTimeline
					matches={matches}
					rosterId={team.id}
					navigate={navigate}
					compact
					view="timeline"
				/>
			</section>
		</div>
	);
}
